import WheatSvg from "@icons/wheat.svg?react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import React from "react";

import type { ChatMessage } from "../market-ai.api";
import { sendChatMessage } from "../market-ai.api";
import { MarketMessage } from "./market-message";

/* eslint-disable lingui/no-unlocalized-strings */
const SUGGESTION_QUERIES = [
  "Show me the wheat price trend for 2025",
  "Compare rapeseed prices across regions in 2024",
  "What is the cheapest location for corn in Q4 2024?",
  "What is the average barley price by quarter in 2025?",
] as const;
/* eslint-enable lingui/no-unlocalized-strings */

const TypingIndicator = ({ text }: { text: string }) => (
  <div className="flex items-center gap-x-3">
    <div
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
      style={{
        background: "linear-gradient(135deg, #168b6b 0%, #0d6b53 100%)",
      }}
    >
      <WheatSvg className="h-3.5 w-3.5 text-white" />
    </div>
    <div
      className="flex items-center gap-x-3 rounded-2xl rounded-tl-sm px-4 py-2.5"
      style={{
        background: "#fff",
        boxShadow: "2px 4px 12px 0px rgba(122,128,147,0.18)",
      }}
    >
      <span
        className="text-sm font-medium tracking-wide"
        style={{ color: "#7a8093" }}
      >
        {text}
      </span>
      <span className="flex gap-x-1">
        {[0, 1, 2].map((i) => (
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            key={i}
            style={{
              animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
              background: "#168b6b",
            }}
          />
        ))}
      </span>
    </div>
  </div>
);

const EmptyState = ({ onSelect }: { onSelect: (q: string) => void }) => {
  const { _ } = useLingui();
  const SUGGESTIONS = [
    { label: _(msg`Vývoj cen pšenice 2025`), query: SUGGESTION_QUERIES[0] },
    {
      label: _(msg`Porovnat řepku napříč regiony`),
      query: SUGGESTION_QUERIES[1],
    },
    {
      label: _(msg`Nejlevnější lokace pro kukuřici`),
      query: SUGGESTION_QUERIES[2],
    },
    { label: _(msg`Průměrná cena ječmene`), query: SUGGESTION_QUERIES[3] },
  ];
  return (
    <div className="flex h-full flex-col items-center justify-center gap-y-8 px-4 py-8">
      {/* Icon + heading */}
      <div className="flex flex-col items-center gap-y-3">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-2xl"
          style={{
            background: "linear-gradient(135deg, #168b6b 0%, #0d6b53 100%)",
            boxShadow: "0 8px 24px rgba(22,139,107,0.32)",
          }}
        >
          <WheatSvg className="h-7 w-7 text-white" />
        </div>
        <div className="text-center">
          <p className="text-xl font-semibold" style={{ color: "#4d5570" }}>
            <Trans>AI Analýza trhu</Trans>
          </p>
          <p className="mt-0.5 text-base" style={{ color: "#7a8093" }}>
            <Trans>Ptejte se na ceny komodit, trendy a srovnání</Trans>
          </p>
        </div>
      </div>

      {/* Suggestion cards */}
      <div className="grid w-full max-w-lg grid-cols-2 gap-3">
        {SUGGESTIONS.map((s) => (
          <button
            className="group relative overflow-hidden rounded-xl px-4 py-3 text-left transition-all duration-150 hover:-translate-y-0.5"
            key={s.query}
            onClick={() => onSelect(s.query)}
            style={{
              background: "#fff",
              border: "1px solid rgba(202,204,209,0.6)",
              boxShadow: "2px 4px 12px 0px rgba(122,128,147,0.18)",
            }}
            type="button"
          >
            <div
              className="absolute inset-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
              style={{
                background:
                  "linear-gradient(135deg, rgba(22,139,107,0.04) 0%, transparent 100%)",
              }}
            />
            <span
              className="relative block text-sm font-medium leading-snug"
              style={{ color: "#4d5570" }}
            >
              {s.label}
            </span>
            <span
              className="relative mt-1 block text-xs"
              style={{ color: "#7a8093" }}
            >
              →
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const MarketChat = () => {
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [input, setInput] = React.useState("");
  const [isPending, setIsPending] = React.useState(false);
  const [statusText, setStatusText] = React.useState<string | null>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isPending]);

  // Auto-grow textarea
  React.useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);

  const submit = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isPending) return;

    setMessages((prev) => [
      ...prev,
      { content: trimmed, id: crypto.randomUUID(), role: "user" },
    ]);
    setInput("");
    setIsPending(true);
    setStatusText(null);

    try {
      const response = await sendChatMessage(
        { message: trimmed },
        setStatusText,
      );
      setMessages((prev) => [
        ...prev,
        {
          content: response.answer,
          id: crypto.randomUUID(),
          role: "assistant",
          sources: response.sources,
          visualization: response.visualization,
        },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          // eslint-disable-next-line lingui/no-unlocalized-strings
          content: error instanceof Error ? error.message : "Nastala chyba.",
          id: crypto.randomUUID(),
          role: "assistant",
          sources: [],
          visualization: null,
        },
      ]);
    } finally {
      setIsPending(false);
      setStatusText(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit(input);
    }
  };

  const isEmpty = messages.length === 0 && !isPending;

  return (
    <>
      {/* Bounce keyframes */}
      {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes msg-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .msg-enter { animation: msg-in 0.2s ease forwards; }
      `}</style>

      <div className="flex h-full flex-col">
        {/* Message area */}
        <div className="flex flex-1 flex-col overflow-y-auto">
          {isEmpty ? (
            <EmptyState onSelect={(q) => void submit(q)} />
          ) : (
            <div className="flex flex-col gap-y-5 px-1 py-4">
              {messages.map((msg) => (
                <div className="msg-enter" key={msg.id}>
                  <MarketMessage message={msg} />
                </div>
              ))}
              {isPending ? (
                <div className="msg-enter">
                  {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
                  <TypingIndicator text={statusText ?? "Přemýšlím…"} />
                </div>
              ) : null}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* Input bar */}
        <div
          className="mt-2 shrink-0 rounded-2xl p-3"
          style={{
            background: "#fff",
            border: "1px solid rgba(202,204,209,0.5)",
            boxShadow: "2px 4px 12px 0px rgba(122,128,147,0.18)",
          }}
        >
          <textarea
            className="w-full resize-none bg-transparent text-base leading-relaxed outline-none"
            disabled={isPending}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            // eslint-disable-next-line lingui/no-unlocalized-strings
            placeholder="Zeptejte se na ceny komodit…"
            ref={textareaRef}
            rows={1}
            style={{ color: "#4d5570", minHeight: "24px" }}
            value={input}
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs" style={{ color: "#caccd1" }}>
              <Trans>Enter pro odeslání</Trans>
              {" · "}
              <Trans>Shift+Enter nový řádek</Trans>
            </span>
            <button
              className="flex items-center gap-x-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white transition-all duration-150 disabled:opacity-40"
              disabled={!input.trim() || isPending}
              onClick={() => void submit(input)}
              style={{
                background:
                  input.trim() && !isPending
                    ? "linear-gradient(135deg, #168b6b 0%, #0d6b53 100%)"
                    : "#caccd1",
              }}
              type="button"
            >
              <Trans>Odeslat</Trans>
              <svg fill="none" height="12" viewBox="0 0 12 12" width="12">
                <path
                  d="M2 6h8M6 2l4 4-4 4"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export { MarketChat };
